// consultar-produtos-nomus -- logica pura (testavel sem Deno nem rede).
//
// Spec: docs/superpowers/specs/2026-09-23-consulta-real-ao-nomus.md
// Substitui a simulacao `nomusGetProduto`, que sorteava preco, saldo, ativo e
// bloqueio (issues #6 e #16 do Kaio, 23/09).
//
// Formato real do GET /produtos/{id}, conferido nas respostas cruas de 05/08
// (uploads/teste-nomus-out/t6_produto_18474.json):
//   ativo, bloquearNovasVendas                       -> booleanos
//   tabelasPreco[]: { id, nome, preco: "1.305,93" }  -> preco e STRING brasileira
//   empresasSetoresEstoque[]: { idEmpresa, saldoEstoqueAtualEmpresa: "-317,90" }
//   dataHoraUltimaModificacao: "04/08/2026 09:41:05" -> horario de Brasilia

export const MAX_IDS = 80;
export const LOTE_PADRAO = 5;
export const LIMITE_MS = 8000;

/** "1.305,93" -> 1305.93 · "-317,90" -> -317.9 · "" / null -> null. */
export function lerNumeroNomus(valor: unknown): number | null {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  const s = String(valor ?? '').trim();
  if (!s) return null;
  // Virgula e decimal; ponto e milhar. Sem virgula, o texto ja e decimal com ponto.
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : null;
}

/** "04/08/2026 09:41:05" (Brasilia, UTC-3) -> ISO em UTC. Invalida -> null. */
export function lerDataNomus(valor: unknown): string | null {
  const m = String(valor ?? '').trim().match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!m) return null;
  const [, d, mes, a, h = '00', min = '00', seg = '00'] = m;
  const data = new Date(`${a}-${mes}-${d}T${h}:${min}:${seg}-03:00`);
  return Number.isNaN(data.getTime()) ? null : data.toISOString();
}

export interface EstadoProduto {
  idProduto: number;
  ok: true;
  ativo: boolean;
  bloquearNovasVendas: boolean;
  precosPorTabela: Record<string, number>;
  saldos: Record<string, number>;
  alteradoEm: string | null;
  ms: number;
}
export interface FalhaProduto { idProduto: number; ok: false; erro: string; ms: number }
export type Resultado = EstadoProduto | FalhaProduto;

/** O JSON do Nomus, no vocabulario do CRM. Tabela sem preco legivel NAO entra
 *  (fica "sem preco na tabela", nunca zero). */
export function estadoDoProduto(idProduto: number, json: Record<string, unknown>, ms: number): EstadoProduto {
  const precosPorTabela: Record<string, number> = {};
  for (const t of (json.tabelasPreco as Array<Record<string, unknown>>) ?? []) {
    const preco = lerNumeroNomus(t?.preco);
    if (t?.id != null && preco != null) precosPorTabela[String(t.id)] = preco;
  }
  const saldos: Record<string, number> = {};
  for (const e of (json.empresasSetoresEstoque as Array<Record<string, unknown>>) ?? []) {
    const saldo = lerNumeroNomus(e?.saldoEstoqueAtualEmpresa);
    if (e?.idEmpresa != null && saldo != null && !(String(e.idEmpresa) in saldos)) saldos[String(e.idEmpresa)] = saldo;
  }
  return {
    idProduto,
    ok: true,
    // Os dois sao estados DIFERENTES (produto 1 inativo, produto 500 bloqueado,
    // medidos em 05/08). Ausente conta como o lado seguro: nao ativo / bloqueado.
    ativo: json.ativo === true,
    bloquearNovasVendas: json.bloquearNovasVendas !== false,
    precosPorTabela,
    saldos,
    alteradoEm: lerDataNomus(json.dataHoraUltimaModificacao),
    ms,
  };
}

/** Entrada da funcao: 1 a 80 ids inteiros positivos, sem repeticao. */
export function validarEntrada(corpo: unknown): { ids: number[] } | { erro: string } {
  const lista = (corpo as { idsProduto?: unknown })?.idsProduto;
  if (!Array.isArray(lista) || lista.length === 0) return { erro: 'Informe os produtos a consultar (idsProduto).' };
  if (lista.some((v) => !Number.isInteger(v) || (v as number) <= 0)) return { erro: 'idsProduto aceita só números inteiros positivos.' };
  const ids = [...new Set(lista as number[])];
  if (ids.length > MAX_IDS) return { erro: `No máximo ${MAX_IDS} produtos por consulta; vieram ${ids.length}.` };
  return { ids };
}

/** Mensagem do erro de UM produto, em portugues, dizendo qual ponta falhou. */
export function mensagemDeFalha(status: number | null, porRelay = false): string {
  if (status === null) return 'O Nomus não respondeu a tempo. Tente de novo.';
  if (status === 404) return 'Produto não encontrado no Nomus.';
  if (status === 401 || status === 403 || status === 406) return 'O Nomus recusou a credencial do CRM.';
  if (porRelay && (status === 502 || status === 503 || status === 504)) return 'O retransmissor não alcançou o Nomus. Tente de novo.';
  if (status === 429) return 'O Nomus pediu para esperar (limite de consultas). Tente de novo em instantes.';
  return `O Nomus respondeu com erro ${status}. Tente de novo.`;
}

/** O Nomus avisa o limite de taxa com HTTP 200 e `{"tempoAteLiberar":"N"}` no
 *  lugar dos dados (scripts/nomus-carteira.sh, medido em 09/2026). Lido como
 *  produto, isso viraria "bloqueado para venda" -- os campos faltam e o lado
 *  seguro e bloqueado. Devolve os segundos pedidos, ou null. */
export function esperaDoNomus(json: unknown): number | null {
  if (!json || typeof json !== 'object' || !('tempoAteLiberar' in (json as object))) return null;
  const n = Number(String((json as Record<string, unknown>).tempoAteLiberar).replace(/\D/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Espera curta o bastante para tentar de novo dentro da mesma consulta. */
export const ESPERA_MAX_S = 10;

export interface Deps {
  /** GET /produtos/{id}. Devolve status e JSON; lanca em timeout/rede. */
  buscarProduto(id: number): Promise<{ status: number; json?: Record<string, unknown> }>;
  agora(): number;
  esperar(ms: number): Promise<void>;
  porRelay: boolean;
}

/** Consulta todos, em lotes de `lote` em paralelo. Um produto que falha nao
 *  derruba os outros: o resultado e SEMPRE um por id, na ordem pedida. */
export async function consultarTodos(deps: Deps, ids: number[], lote = LOTE_PADRAO): Promise<Resultado[]> {
  const tamanho = Math.min(20, Math.max(1, Math.floor(lote) || LOTE_PADRAO));
  const saida: Resultado[] = [];
  for (let i = 0; i < ids.length; i += tamanho) {
    const bloco = ids.slice(i, i + tamanho);
    saida.push(...await Promise.all(bloco.map(async (id): Promise<Resultado> => {
      const t0 = deps.agora();
      try {
        let r = await deps.buscarProduto(id);
        let espera = r.status === 200 ? esperaDoNomus(r.json) : null;
        if (espera !== null && espera <= ESPERA_MAX_S) {
          await deps.esperar((espera + 1) * 1000);
          r = await deps.buscarProduto(id);
          espera = r.status === 200 ? esperaDoNomus(r.json) : null;
        }
        const ms = deps.agora() - t0;
        if (espera !== null) {
          return { idProduto: id, ok: false, erro: `O Nomus pediu para esperar ${espera} s (limite de consultas). Tente de novo em instantes.`, ms };
        }
        if (r.status !== 200 || !r.json) return { idProduto: id, ok: false, erro: mensagemDeFalha(r.status, deps.porRelay), ms };
        return estadoDoProduto(id, r.json, ms);
      } catch {
        return { idProduto: id, ok: false, erro: mensagemDeFalha(null), ms: deps.agora() - t0 };
      }
    })));
  }
  return saida;
}

// ---------------------------------------------------------------------------
// A regra de envio. Mora AQUI e e importada pela tela, para existir uma so.
//
// Decisao do Kaio (25/09), que substitui a de 23/09: o preco certo e o do FATOR
// do CRM, "mantenha os centavos da forma que o fator demonstrar, isso nao pode
// ser um impeditivo para o pedido seguir". E: "o valor so deve ser travado caso
// seja erro de logica; ajustado manualmente ou por desconto, deve ser mantido o
// acordado com o cliente". Entao:
//   - trava: sem id do Nomus, consulta que falhou, bloqueado, inativo, e item
//     SEM PRECO no CRM (o erro de logica);
//   - so informa: preco diferente do Nomus, ou o Nomus sem preco nessa tabela;
//   - negociado a mao passa como esta.
// Antes, preco diferente da tabela do Nomus travava ate "Alinhar a tabela".
// ---------------------------------------------------------------------------
export type Situacao =
  | 'ok' | 'negociado' | 'divergente' | 'nomus_sem_preco' | 'sem_preco' | 'bloqueado' | 'inativo' | 'sem_id' | 'erro';

export interface ItemParaConferir { idProduto?: number | null; precoUnitario: number; precoManual?: boolean }

export function situacaoDoItem(item: ItemParaConferir, r: Resultado | undefined, idTabelaNomus: number | null): Situacao {
  if (!item.idProduto) return 'sem_id';
  if (!r || !r.ok) return 'erro';
  if (r.bloquearNovasVendas) return 'bloqueado';
  if (!r.ativo) return 'inativo';
  if (item.precoManual) return 'negociado';
  if (!(Number(item.precoUnitario) > 0)) return 'sem_preco';
  const precoTabela = idTabelaNomus != null ? r.precosPorTabela[String(idTabelaNomus)] : undefined;
  if (precoTabela == null) return 'nomus_sem_preco';
  return Math.abs(precoTabela - item.precoUnitario) < 0.005 ? 'ok' : 'divergente';
}

/** Situacoes que impedem o POST /pedidos. Diferenca de preco com o Nomus NAO
 *  esta aqui: o preco que vai e o do CRM (decisao do Kaio, 25/09). */
export const BLOQUEIA_ENVIO: ReadonlySet<Situacao> = new Set(['sem_preco', 'bloqueado', 'inativo', 'sem_id', 'erro']);
